/**
 * Shadow of VLT 8.0.0-alpha.28 components/LanguageSelector/LanguageSelector.tsx.
 *
 * WHY: VLT's selector renders `langKey.slice(0, 2)` — the first two letters of
 * the language CODE (en -> "EN", zh-cn -> "ZH"). For the Chinese option that
 * shows the Latin token "ZH" instead of the native script. This shadow renders
 * the NATIVE language name instead (en -> "English", zh-cn -> "简体中文"),
 * matching the fleet precedent (arcab LanguageSelector langNames map; originally
 * DR-2026-054). Operator-reported (DR-2026-264 follow-up: the 简体中文 label).
 *
 * DELTAS vs upstream VLT LanguageSelector.tsx: types dropped (.jsx); the single
 * CUSTOMIZATION line `{langKey.slice(0, 2)}` is replaced by a native-name lookup
 * (langNames[lang], falling back to Volto's langmap nativeName, then the code).
 * Everything else — the multilingual guard, the Helmet lang tag, the translation
 * link resolution, the `selected` class, aria/title — is byte-identical, so the
 * VLT CSS (.language-selector) and behaviour apply unchanged.
 *
 * The backend language codes are LOWERCASE ('en' / 'zh-cn') per the wantai
 * languageroots; both casings are mapped for safety.
 *
 * Re-verify against upstream on any VLT bump.
 */
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import cx from 'classnames';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import langmap from '@plone/volto/helpers/LanguageMap/LanguageMap';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import { toReactIntlLang } from '@plone/volto/helpers/Utils/Utils';
import { defineMessages, useIntl } from 'react-intl';

const messages = defineMessages({
  switchLanguageTo: { id: 'Switch to', defaultMessage: 'Switch to' },
});

// Native language names, keyed by the tenant's backend language codes.
const langNames = {
  en: 'English',
  'zh-cn': '简体中文',
  'zh-CN': '简体中文',
};

const LanguageSelector = ({ onClickAction = () => {} }) => {
  const intl = useIntl();
  const currentLang = useSelector((state) => state.intl.locale);
  const translations = useSelector(
    (state) => state.content.data?.['@components']?.translations?.items,
  );
  const isMultilingual = useSelector(
    (state) => state.site.data.features?.multilingual,
  );
  const availableLanguages = useSelector(
    (state) => state.site.data?.['plone.available_languages'],
  );

  return isMultilingual ? (
    <div className="language-selector">
      {availableLanguages?.map((lang) => {
        const langKey = lang;
        const translation = translations?.find((t) => t.language === lang);
        const label = langNames[lang] || langmap[langKey]?.nativeName || langKey;
        return (
          <Link
            aria-label={`${intl.formatMessage(
              messages.switchLanguageTo,
            )} ${label.toLowerCase()}`}
            className={cx({ selected: toReactIntlLang(lang) === currentLang })}
            to={translation ? flattenToAppURL(translation['@id']) : `/${lang}`}
            title={label}
            onClick={() => {
              onClickAction();
            }}
            key={`language-selector-${lang}`}
          >
            {/* START CUSTOMIZATION — native name, not the 2-letter code */}
            {label}
            {/*  END CUSTOMIZATION */}
          </Link>
        );
      })}
    </div>
  ) : (
    <Helmet>
      <html lang={toReactIntlLang(currentLang)} />
    </Helmet>
  );
};

export default LanguageSelector;
